import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import ProfilePictureUpload from '../ProfilePictureUpload';
import ProfilePictureZoom from '../ProfilePictureZoom';
import CredentialManager from './sections/CredentialManager';
import VerificationSidebar from './sections/VerificationSidebar';
import ComplianceSection from './sections/ComplianceSection';
import {
  Building2, User, Mail, MapPin, Phone, ShieldCheck, CheckCircle,
  Truck, Package, Globe, ExternalLink, FileCheck,
  Users, Briefcase, ChevronRight, FileText, Award, Route,
  ToggleLeft, ToggleRight
} from 'lucide-react';
import { toast } from 'sonner';
import { mediaUrl } from '../../utils/media';

const LOAD_BOARDS = [
  { name: 'DAT One', tag: 'premium', url: 'https://www.dat.com', desc: 'Industry leader, deep rate analytics' },
  { name: 'Truckstop', tag: 'premium', url: 'https://www.truckstop.com', desc: 'Broker credit & days-to-pay data' },
  { name: 'Amazon Relay', tag: 'specialized', url: 'https://relay.amazon.com', desc: 'Direct Amazon Middle Mile freight' },
  { name: 'TruckSmarter', tag: 'free', url: 'https://www.trucksmarter.com', desc: '100% free aggregated loads' }
];

const TAG_STYLE = { premium: 'bg-amber-50 text-amber-700 border-amber-200', specialized: 'bg-blue-50 text-blue-700 border-blue-200', free: 'bg-green-50 text-green-700 border-green-200' };

const CarrierOwnerProfile = ({ profile, user, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [badges, setBadges] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [verifiedVisible, setVerifiedVisible] = useState(profile?.verified_visible !== false);
  const [ratingsVisible, setRatingsVisible] = useState(profile?.ratings_visible !== false);
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    company_name: profile?.company_name || '',
    phone: profile?.phone || '',
    location: profile?.location || '',
    bio: profile?.bio || '',
    mc_number: profile?.mc_number || '',
    dot_number: profile?.dot_number || '',
    insurance_info: profile?.insurance_info || '',
    equipment_types: profile?.equipment_types || '',
    service_areas: profile?.service_areas || ''
  });

  useEffect(() => { fetchBadges(); fetchAvailableDrivers(); }, []);

  const fetchBadges = async () => {
    try {
      const response = await api.get('/badges/list');
      setBadges(response.data);
    } catch (error) {
      // Badge fetch handled silently
    }
  };

  const fetchAvailableDrivers = async () => {
    try {
      const res = await api.get('/profile/open-to-work');
      setAvailableDrivers(res.data.drivers || []);
    } catch {
      // Silently handled
    }
  };

  const toggleVisibility = async (field) => {
    try {
      const { data } = await api.put(`/profile/toggle-visibility?field=${field}`);
      if (field === 'verified_visible') setVerifiedVisible(data[field]);
      else setRatingsVisible(data[field]);
      toast.success(`${field === 'verified_visible' ? 'Verified badge' : 'Ratings'} ${data[field] ? 'shown' : 'hidden'} on your profile`);
    } catch {
      toast.error('Failed to update visibility');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/profile/me', formData);
      toast.success('Profile updated successfully');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900 via-green-800 to-emerald-900"></div>
        <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`}}></div>

        <div className="relative px-8 py-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <ProfilePictureZoom src={user?.profile_picture} alt={formData.company_name || 'Carrier'} size="w-24 h-24">
                <ProfilePictureUpload currentPicture={user?.profile_picture} onUploadSuccess={() => { if (onUpdate) onUpdate(); }} />
              </ProfilePictureZoom>
              <div>
                <span className="inline-block px-3 py-1 bg-emerald-500/20 backdrop-blur-sm rounded-full text-xs font-semibold text-emerald-300 border border-emerald-400/30 mb-2 tracking-wider uppercase">
                  Carrier / Owner
                </span>
                <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="carrier-profile-title">
                  {formData.company_name || profile?.name || 'Carrier Profile'}
                </h1>
                <div className="flex items-center gap-4 mt-2 text-emerald-300/70 text-sm">
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{profile?.location || 'Location not set'}</span>
                  {profile?.mc_number && <span className="flex items-center gap-1 font-mono text-xs bg-white/10 px-2 py-0.5 rounded">{profile.mc_number}</span>}
                </div>
              </div>
            </div>

            {/* Verification Status */}
            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-2">
                <button onClick={() => toggleVisibility('verified_visible')}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 hover:bg-white/20 transition-colors"
                  data-testid="toggle-verified-btn">
                  {verifiedVisible ? <ToggleRight className="h-4 w-4 text-green-400" /> : <ToggleLeft className="h-4 w-4 text-slate-400" />}
                  <span className="text-xs text-white font-medium">Verified {verifiedVisible ? 'On' : 'Off'}</span>
                </button>
                <button onClick={() => toggleVisibility('ratings_visible')}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 hover:bg-white/20 transition-colors"
                  data-testid="toggle-ratings-btn">
                  {ratingsVisible ? <ToggleRight className="h-4 w-4 text-green-400" /> : <ToggleLeft className="h-4 w-4 text-slate-400" />}
                  <span className="text-xs text-white font-medium">Ratings {ratingsVisible ? 'On' : 'Off'}</span>
                </button>
              </div>
              <div className="bg-white/5 backdrop-blur-md rounded-xl p-5 border border-white/10 min-w-[180px]" data-testid="carrier-verification-card">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${profile?.verification_status === 'verified' ? 'bg-green-500/20' : 'bg-amber-500/20'}`}>
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-400 uppercase tracking-wider">Verification</p>
                  <p className="text-sm font-bold text-white capitalize">{profile?.verification_status || 'Pending'}</p>
                </div>
              </div>
              {profile?.dot_number && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-[10px] text-slate-500">DOT Number</p>
                  <p className="font-mono text-sm font-bold text-white">{profile.dot_number}</p>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Business Information */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-emerald-500" /> Business Information
              </h2>
              <button onClick={() => setEditing(!editing)} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium" data-testid="edit-carrier-profile-btn">
                {editing ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>

            {editing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { label: 'Contact Name', key: 'name', testId: 'carrier-name-input' },
                    { label: 'Company Name', key: 'company_name', testId: 'carrier-company-input', placeholder: 'ABC Trucking LLC' },
                    { label: 'MC Number', key: 'mc_number', testId: 'carrier-mc-input', placeholder: 'MC-123456' },
                    { label: 'DOT Number', key: 'dot_number', testId: 'carrier-dot-input', placeholder: 'DOT-1234567' },
                    { label: 'Phone', key: 'phone', testId: 'carrier-phone-input', type: 'tel' },
                    { label: 'Location', key: 'location', testId: 'carrier-location-input', placeholder: 'Houston, TX' },
                  ].map(({ label, key, testId, placeholder, type }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-slate-600 mb-1">{label}</label>
                      <input type={type || 'text'} value={formData[key]} onChange={(e) => setFormData({...formData, [key]: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm" placeholder={placeholder} data-testid={testId} />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Insurance Coverage</label>
                  <input type="text" value={formData.insurance_info} onChange={(e) => setFormData({...formData, insurance_info: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm" placeholder="$1M liability, $100K cargo" data-testid="carrier-insurance-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Equipment Types</label>
                  <input type="text" value={formData.equipment_types} onChange={(e) => setFormData({...formData, equipment_types: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm" placeholder="26ft Box Truck, Sprinter Van, Cargo Van" data-testid="carrier-equipment-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Service Areas</label>
                  <input type="text" value={formData.service_areas} onChange={(e) => setFormData({...formData, service_areas: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm" placeholder="Texas, Oklahoma, Louisiana" data-testid="carrier-service-areas-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Business Description</label>
                  <textarea value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm" placeholder="Tell us about your business..." data-testid="carrier-bio-input" />
                </div>
                <button type="submit" disabled={loading} className="bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="save-carrier-profile-btn">
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { icon: User, label: 'Contact', value: profile?.name },
                    { icon: Building2, label: 'Company', value: profile?.company_name },
                    { icon: FileCheck, label: 'MC Number', value: profile?.mc_number, highlight: true },
                    { icon: FileCheck, label: 'DOT Number', value: profile?.dot_number, highlight: true },
                    { icon: Phone, label: 'Phone', value: profile?.phone },
                    { icon: MapPin, label: 'Location', value: profile?.location },
                  ].map(({ icon: Icon, label, value, highlight }) => (
                    <div key={label} className={`flex items-center gap-3 p-3 rounded-lg ${highlight ? 'bg-emerald-50 border border-emerald-100' : 'bg-slate-50/80'}`}>
                      <Icon className={`h-4 w-4 flex-shrink-0 ${highlight ? 'text-emerald-500' : 'text-slate-400'}`} />
                      <div>
                        <p className={`text-[11px] uppercase tracking-wider font-medium ${highlight ? 'text-emerald-500' : 'text-slate-400'}`}>{label}</p>
                        <p className="text-sm font-medium text-slate-800">{value || 'Not set'}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {profile?.insurance_info && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-1">
                      <ShieldCheck className="h-4 w-4 text-blue-500" />
                      <span className="text-[11px] text-blue-500 uppercase tracking-wider font-medium">Insurance</span>
                    </div>
                    <p className="text-sm text-blue-800 font-medium">{profile.insurance_info}</p>
                  </div>
                )}

                {profile?.equipment_types && (
                  <div className="pt-4 mt-3 border-t border-slate-100">
                    <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-2 flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> Equipment</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.equipment_types.split(',').map((t) => (
                        <span key={t.trim()} className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-medium border border-emerald-100">{t.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {profile?.service_areas && (
                  <div className="pt-4 mt-3 border-t border-slate-100">
                    <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-2 flex items-center gap-1"><Route className="h-3.5 w-3.5" /> Service Areas</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.service_areas.split(',').map((a) => (
                        <span key={a.trim()} className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-xs font-medium">{a.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {profile?.bio && (
                  <div className="pt-4 mt-3 border-t border-slate-100">
                    <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-1">About</p>
                    <p className="text-sm text-slate-700 leading-relaxed">{profile.bio}</p>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* Credentials */}
          <CredentialManager badges={badges} onBadgesChange={fetchBadges} />

          {/* Available Drivers */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white border border-slate-200 rounded-xl p-6" data-testid="available-drivers-section">
            <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-green-500" /> Available Drivers
            </h2>
            <p className="text-sm text-slate-500 mb-4">Drivers currently open to work opportunities</p>
            {availableDrivers.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                <Users className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No drivers are currently open to work</p>
              </div>
            ) : (
              <div className="space-y-3">
                {availableDrivers.map((d) => (
                  <div key={d.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-green-200 transition-colors" data-testid={`available-driver-${d.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {d.profile_picture ? (
                          <img src={mediaUrl(d.profile_picture)} alt={d.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{d.name}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          {d.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{d.location}</span>}
                          {d.experience_years && <span>{d.experience_years} yrs exp</span>}
                          {d.license_type && <span>{d.license_type}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle className="h-3 w-3" />Available
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Carrier Capabilities */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-500" /> Carrier Capabilities
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100 text-center">
                <Users className="h-7 w-7 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-emerald-900">Hire Drivers</p>
                <p className="text-xs text-emerald-600 mt-0.5">Post jobs for qualified drivers</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 text-center">
                <Package className="h-7 w-7 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-blue-900">Post Loads</p>
                <p className="text-xs text-blue-600 mt-0.5">List available freight</p>
              </div>
              <div className="p-4 bg-violet-50 rounded-lg border border-violet-100 text-center">
                <Globe className="h-7 w-7 text-violet-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-violet-900">Dispatch Partners</p>
                <p className="text-xs text-violet-600 mt-0.5">Work with dispatch services</p>
              </div>
            </div>
          </motion.div>

          {/* Load Boards */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Globe className="h-5 w-5 text-emerald-500" /> Load Boards
              </h2>
              <Link to="/external-job-boards" className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                View All <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {LOAD_BOARDS.map((b) => (
                <a key={b.name} href={b.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50/50 transition-all group"
                  data-testid={`load-board-${b.name.toLowerCase().replace(/\s/g, '-')}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900 group-hover:text-emerald-700">{b.name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${TAG_STYLE[b.tag]}`}>{b.tag}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{b.desc}</p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-slate-300 group-hover:text-emerald-500 flex-shrink-0" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-emerald-900 rounded-xl p-6 text-white"
          >
            <h2 className="text-lg font-bold mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Link to="/jobs" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/5">
                <Briefcase className="h-5 w-5 text-emerald-400" />
                <div><p className="text-sm font-medium">Browse Jobs</p><p className="text-[11px] text-emerald-300/60">Find freight</p></div>
                <ChevronRight className="h-4 w-4 text-emerald-500/40 ml-auto" />
              </Link>
              <Link to="/referrals" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/5">
                <Award className="h-5 w-5 text-amber-400" />
                <div><p className="text-sm font-medium">Referrals</p><p className="text-[11px] text-emerald-300/60">Grow network</p></div>
                <ChevronRight className="h-4 w-4 text-emerald-500/40 ml-auto" />
              </Link>
              <Link to="/resources" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/5">
                <FileText className="h-5 w-5 text-sky-400" />
                <div><p className="text-sm font-medium">Resources</p><p className="text-[11px] text-emerald-300/60">Guides</p></div>
                <ChevronRight className="h-4 w-4 text-emerald-500/40 ml-auto" />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <VerificationSidebar profile={profile} badges={badges} color="green" />

          {/* RMIS & Highway Compliance */}
          <ComplianceSection />

          {/* Business Metrics */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}
            className="bg-white border border-slate-200 rounded-xl p-5"
          >
            <h3 className="text-sm font-bold text-slate-900 mb-3">Business Metrics</h3>
            <div className="space-y-2.5">
              <div className="flex justify-between items-center p-2.5 bg-slate-50 rounded-lg">
                <span className="text-xs text-slate-500">Jobs Applied</span>
                <span className="text-sm font-semibold text-slate-800">0</span>
              </div>
              <div className="flex justify-between items-center p-2.5 bg-slate-50 rounded-lg">
                <span className="text-xs text-slate-500">Referrals</span>
                <span className="text-sm font-semibold text-slate-800">0</span>
              </div>
              <div className="flex justify-between items-center p-2.5 bg-slate-50 rounded-lg">
                <span className="text-xs text-slate-500">Credentials</span>
                <span className="text-sm font-semibold text-slate-800">{badges.length}</span>
              </div>
            </div>
          </motion.div>

          {/* Upgrade CTA */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-xl p-5"
          >
            <h3 className="text-sm font-bold mb-2">Your Business Profile</h3>
            <p className="text-xs text-amber-100 mb-3">Click the button to view business profile page.</p>
            <Link to="/profile" className="block w-full bg-white text-orange-600 px-4 py-2 rounded-lg text-xs font-semibold text-center hover:bg-orange-50 transition-colors" data-testid="carrier-upgrade-cta">
              View Profile
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CarrierOwnerProfile;
